import React, { useState } from 'react';
import { CalendarIcon, X } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Label } from '@vaiu/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@vaiu/ui/select';
import { Input } from '@vaiu/ui/input';
import { Badge } from '@vaiu/ui/badge';
import { Calendar } from '@vaiu/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@vaiu/ui/popover';
import { format } from 'date-fns';

interface TransactionFiltersProps {
  filters: {
    type?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  };
  onFiltersChange: (filters: any) => void;
}

const TransactionFilters: React.FC<TransactionFiltersProps> = ({
  filters,
  onFiltersChange
}) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [startDate, setStartDate] = useState<Date | undefined>(
    filters.startDate ? new Date(filters.startDate) : undefined
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    filters.endDate ? new Date(filters.endDate) : undefined
  );

  const transactionTypes = [
    { value: 'send', label: 'Sent' },
    { value: 'receive', label: 'Received' },
    { value: 'deposit', label: 'Deposit' },
    { value: 'withdraw', label: 'Withdrawal' },
    { value: 'redeem', label: 'Redeemed' }
  ];

  const transactionStatuses = [
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'expired', label: 'Expired' },
    { value: 'partial', label: 'Partial' },
    { value: 'unfunded', label: 'Unfunded' },
    { value: 'revealed', label: 'Revealed' },
    { value: 'transferred', label: 'Transferred' }
  ];

  const handleFilterChange = (key: string, value: string | undefined) => {
    const newFilters = {
      ...localFilters,
      [key]: value || undefined
    };
    
    // Remove undefined values
    Object.keys(newFilters).forEach(k => {
      if (newFilters[k] === undefined) {
        delete newFilters[k];
      }
    });
    
    setLocalFilters(newFilters);
  };

  const handleDateChange = (date: Date | undefined, type: 'start' | 'end') => {
    if (type === 'start') {
      setStartDate(date);
      handleFilterChange('startDate', date ? date.toISOString().split('T')[0] : undefined);
    } else {
      setEndDate(date);
      handleFilterChange('endDate', date ? date.toISOString().split('T')[0] : undefined);
    }
  };

  const applyFilters = () => {
    onFiltersChange(localFilters);
  };

  const clearFilters = () => {
    const emptyFilters = {};
    setLocalFilters(emptyFilters);
    setStartDate(undefined);
    setEndDate(undefined);
    onFiltersChange(emptyFilters);
  };

  const getActiveFiltersCount = () => {
    return Object.keys(localFilters).length;
  };

  const removeFilter = (key: string) => {
    const newFilters = { ...localFilters };
    delete newFilters[key];
    setLocalFilters(newFilters);
    
    if (key === 'startDate') setStartDate(undefined);
    if (key === 'endDate') setEndDate(undefined);
    
    onFiltersChange(newFilters);
  };

  return (
    <div className="space-y-6">
      {/* Filter Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Transaction Type */}
        <div className="space-y-2">
          <Label>Transaction Type</Label>
          <Select
            value={localFilters.type || ''}
            onValueChange={(value) => handleFilterChange('type', value === 'all' ? undefined : value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {transactionTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Status */}
        <div className="space-y-2">
          <Label>Status</Label>
          <Select
            value={localFilters.status || ''}
            onValueChange={(value) => handleFilterChange('status', value === 'all' ? undefined : value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {transactionStatuses.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Start Date */}
        <div className="space-y-2">
          <Label>Start Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate ? format(startDate, 'PPP') : 'Pick a date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => handleDateChange(date, 'start')}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* End Date */}
        <div className="space-y-2">
          <Label>End Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {endDate ? format(endDate, 'PPP') : 'Pick a date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={(date) => handleDateChange(date, 'end')}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Active Filters */}
      {getActiveFiltersCount() > 0 && (
        <div className="space-y-2">
          <Label>Active Filters ({getActiveFiltersCount()})</Label>
          <div className="flex flex-wrap gap-2">
            {localFilters.type && (
              <Badge variant="secondary" className="flex items-center space-x-1">
                <span>Type: {transactionTypes.find(t => t.value === localFilters.type)?.label}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 hover:bg-transparent"
                  onClick={() => removeFilter('type')}
                >
                  <X className="w-3 h-3" />
                </Button>
              </Badge>
            )}
            
            {localFilters.status && (
              <Badge variant="secondary" className="flex items-center space-x-1">
                <span>Status: {transactionStatuses.find(s => s.value === localFilters.status)?.label}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 hover:bg-transparent"
                  onClick={() => removeFilter('status')}
                >
                  <X className="w-3 h-3" />
                </Button>
              </Badge>
            )}
            
            {localFilters.startDate && (
              <Badge variant="secondary" className="flex items-center space-x-1">
                <span>From: {format(new Date(localFilters.startDate), 'MMM d')}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 hover:bg-transparent"
                  onClick={() => removeFilter('startDate')}
                >
                  <X className="w-3 h-3" />
                </Button>
              </Badge>
            )}
            
            {localFilters.endDate && (
              <Badge variant="secondary" className="flex items-center space-x-1">
                <span>To: {format(new Date(localFilters.endDate), 'MMM d')}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 hover:bg-transparent"
                  onClick={() => removeFilter('endDate')}
                >
                  <X className="w-3 h-3" />
                </Button>
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-2">
        <Button onClick={applyFilters}>
          Apply Filters
        </Button>
        <Button 
          variant="outline" 
          onClick={clearFilters}
          disabled={getActiveFiltersCount() === 0}
        >
          Clear All
        </Button>
      </div>
    </div>
  );
};

export default TransactionFilters; 